import type {
  AccessWindowDraft,
  AttachmentDraft,
  JobDraft,
  JobPayload,
  JobRecord,
  JobScopeDraft,
  JobTimingDraft,
  LineItemDraft,
  Priority,
  ReportedBy,
  TimingRequirement,
} from "../types";
import { DEFAULT_TAX_RATE_BPS, costSummary } from "../../costing/costing";
import { validateScopeDraft } from "../../costing/types";
import { emptyJobSchedule, toJobSchedulePayload } from "./schedule";

export const PRIORITIES: readonly Priority[] = ["Routine", "High", "Urgent"];

export const REPORTED_BY: readonly ReportedBy[] = ["Tenant", "Owner", "Property manager"];

export const TIMING_REQUIREMENTS: readonly TimingRequirement[] = [
  "No specific date",
  "Preferred by",
  "Must be completed by",
];

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

/** "24 KB" / "1.4 MB". Sizes come from the picked file, never invented. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Stable key for a staged file. Two files may share a name; keys may not. */
export function attachmentId(name: string): string {
  return `${crypto.randomUUID()}-${name}`;
}

/*
 * The line-item vocabulary belongs to `features/costing` — the invoice builder
 * edits the same rows with no job involved. Re-exported so job modules keep
 * importing it from here.
 */
export { emptyLineItem } from "../../costing/types";

/** Empty scope block: the state a job has when Page 2 was never used. */
export function emptyJobScope(): JobScopeDraft {
  return { description: "", taxRateBps: DEFAULT_TAX_RATE_BPS, lineItems: [] };
}

/**
 * A job's headline value in integer cents, derived — never typed (0013):
 * the Scope total (line items incl. GST) when the job has line
 * items, otherwise the manual price estimate from Page 1. Mirrors the
 * server's listJobsTotals arithmetic exactly.
 */
export function jobHeadlineValueCents(draft: Pick<JobDraft, "scope" | "priceEstimate">): number {
  if (draft.scope.lineItems.length > 0) {
    return costSummary(draft.scope.lineItems, draft.scope.taxRateBps).totalCents;
  }
  return draft.priceEstimate ?? 0;
}

export function emptyJobTiming(): JobTimingDraft {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return {
    requirement: "No specific date",
    requestedNotBeforeOn: "",
    requestedCompletionOn: "",
    reason: "",
    timezone,
    accessWindows: [],
    updatedAt: null,
    source: null,
  };
}

export function emptyJobDraft(): JobDraft {
  return {
    checklists: [],
    clientName: "",
    clientId: undefined,
    clientContactId: undefined,
    clientContactName: "",
    propertyAddress: "",
    propertyConfirmed: false,
    description: "",
    priority: "Routine",
    reportedBy: "Owner",
    timing: emptyJobTiming(),
    schedule: emptyJobSchedule(),
    accessNotes: "",
    occupantName: "",
    occupantPhone: "",
    attachments: [],
    requireQuoteApproval: false,
    refExternal: "",
    refInternal: "",
    priceEstimate: 0,
    scope: emptyJobScope(),
  };
}

/*
 * Money conversion now lives in `src/lib/money` — it is shared with the invoice
 * builder, which has no job to speak of. Re-exported here so the many job
 * modules that reach for it through jobForm keep working unchanged.
 */
export { centsToInputValue, formatPriceCents, parsePriceDollars } from "../../../lib/money";

/**
 * Pure timing validation. Returns the first failure as a human message, or "".
 */
export function validateJobTiming(draft: JobTimingDraft): string {
  if (draft.requirement !== "No specific date" && !DATE_PATTERN.test(draft.requestedCompletionOn)) {
    return "Add the requested completion date";
  }
  if (
    draft.requestedNotBeforeOn &&
    (!DATE_PATTERN.test(draft.requestedNotBeforeOn) ||
      (draft.requestedCompletionOn && draft.requestedNotBeforeOn > draft.requestedCompletionOn))
  ) {
    return "Not-before date must be on or before the requested completion date";
  }
  if (draft.requirement === "Must be completed by" && !draft.reason.trim()) {
    return "Explain why the must-complete date is required";
  }
  if (draft.reason.trim().length > 1000) {
    return "Timing reason must be 1,000 characters or fewer";
  }
  if (!draft.timezone.trim()) return "Add the property time zone for access windows";
  for (const [index, window] of draft.accessWindows.entries()) {
    if (
      !DATE_PATTERN.test(window.availableOn) ||
      !TIME_PATTERN.test(window.startTime) ||
      !TIME_PATTERN.test(window.endTime)
    ) {
      return `Complete the date and times for access window ${index + 1}`;
    }
    if (window.endTime <= window.startTime) {
      return `Access window ${index + 1} must end after it starts`;
    }
  }
  return "";
}

/**
 * First failure wins, top-to-bottom, so the message always points at the highest
 * field the user still has to fix. `knownAddresses` is optional: when supplied,
 * the property must resolve to a known address rather than free text.
 */
export function validateJobDraft(d: JobDraft, knownAddresses: string[] = []): string {
  if (!d.propertyAddress.trim())
    return "Enter the property this request belongs to before submitting. Your draft is kept.";
  if (knownAddresses.length > 0 && knownAddresses.indexOf(d.propertyAddress) === -1)
    return "Pick a property from the list; that address is not known. Your draft is kept.";
  if (!d.propertyConfirmed)
    return "Confirm the property is correct before submitting. Your draft is kept.";
  if (!d.description.trim())
    return "Add a short description before submitting. Your draft is kept.";
  if (d.reportedBy === "Tenant" && !d.occupantName.trim() && !d.occupantPhone.trim())
    return "Add a tenant name or phone number so the vendor can arrange access. Your draft is kept.";
  const timingError = validateJobTiming(d.timing);
  if (timingError) return `${timingError}. Your draft is kept.`;
  return "";
}

/**
 * Page-2 validation. The rule is shared with the invoice builder — a priced row
 * has to be coherent, an untouched one is left alone — so it lives with the
 * editor those two surfaces share.
 */
export function validateJobScopeDraft(scope: JobScopeDraft): string {
  return validateScopeDraft(scope);
}

/** Row title from the first sentence of the description (max 60 chars). */
export function deriveTitle(description: string): string {
  const firstLine = description.trim().split(/\r?\n/)[0].trim();
  const bare = firstLine.split(/[.!?]/)[0].trim() || firstLine;
  return bare.length > 60 ? `${bare.slice(0, 57).trimEnd()}…` : bare;
}

export interface BuildJobPayloadContext {
  jobId: string;
  submittedBy: string;
}

/**
 * The serializable record of a submission. Built on every submit, independent of
 * any UI state, and is what a persistence layer should receive.
 */
export function buildJobPayload(draft: JobDraft, ctx: BuildJobPayloadContext): JobPayload {
  return {
    schemaVersion: 1,
    submittedAt: new Date().toISOString(),
    jobId: ctx.jobId,
    title: deriveTitle(draft.description),
    submittedBy: ctx.submittedBy,
    client: { name: draft.clientName.trim(), id: draft.clientId },
    property: { address: draft.propertyAddress.trim(), confirmed: draft.propertyConfirmed },
    description: draft.description.trim(),
    priority: draft.priority,
    reportedBy: draft.reportedBy,
    timing: {
      requirement: draft.timing.requirement,
      requestedNotBeforeOn: draft.timing.requestedNotBeforeOn || null,
      requestedCompletionOn:
        draft.timing.requirement === "No specific date" ? null : draft.timing.requestedCompletionOn || null,
      reason: draft.timing.reason.trim() || null,
      timezone: draft.timing.timezone.trim(),
      accessWindows: draft.timing.accessWindows.map((window: AccessWindowDraft) => ({
        availableOn: window.availableOn,
        startTime: window.startTime,
        endTime: window.endTime,
      })),
    },
    schedule: toJobSchedulePayload(draft.schedule),
    accessNotes: draft.accessNotes.trim(),
    occupant: { name: draft.occupantName.trim(), phone: draft.occupantPhone.trim() },
    attachments: draft.attachments.map((attachment: AttachmentDraft) => ({
      name: attachment.name,
      size: attachment.size,
      type: attachment.type,
    })),
    requireQuoteApproval: draft.requireQuoteApproval,
  };
}

/**
 * Rebuilds an editable {@link JobDraft} from a persisted {@link JobRecord} so an
 * opened card can be pre-filled exactly as it was submitted.
 */
export function jobRecordToDraft(record: JobRecord): JobDraft {
  return {
    checklists: structuredClone(record.checklists ?? []),
    clientName: record.clientName,
    clientId: record.clientId,
    clientContactId: record.clientContactId,
    clientContactName: record.clientContactName ?? "",
    propertyAddress: record.propertyAddress,
    propertyConfirmed: record.propertyConfirmed,
    description: record.description,
    priority: record.priority,
    reportedBy: record.reportedBy,
    timing: {
      requirement: record.timing.requirement,
      requestedNotBeforeOn: record.timing.requestedNotBeforeOn ?? "",
      requestedCompletionOn: record.timing.requestedCompletionOn ?? "",
      reason: record.timing.reason ?? "",
      timezone: record.timing.timezone,
      accessWindows: record.timing.accessWindows.map(
        (window): AccessWindowDraft => ({
          id: `${window.availableOn}-${window.startTime}-${window.endTime}`,
          availableOn: window.availableOn,
          startTime: window.startTime,
          endTime: window.endTime,
        }),
      ),
      updatedAt: record.timing.updatedAt,
      source: record.timing.source,
    },
    // The client-scoped projection nulls `action` and `internalNote`, so a
    // portal caller's draft simply has nothing in those fields to edit.
    schedule: {
      action: record.schedule?.action ?? "",
      intendedOn: record.schedule?.intendedOn ?? "",
      precision: record.schedule?.precision ?? "",
      internalNote: record.schedule?.internalNote ?? "",
      updatedAt: record.schedule?.updatedAt ?? null,
      updatedBy: record.schedule?.updatedBy ?? null,
    },
    accessNotes: record.accessNotes,
    occupantName: record.occupantName,
    occupantPhone: record.occupantPhone,
    // Rows that exist server-side (with a stored file) are flagged so the UI
    // can offer download/delete instead of treating them as staged files.
    attachments: record.attachments.map((attachment) => ({ ...attachment, persisted: true })),
    requireQuoteApproval: record.requireQuoteApproval,
    refExternal: record.refExternal ?? "",
    refInternal: record.refInternal ?? "",
    priceEstimate: record.priceEstimate ?? 0,
    scope: {
      description: record.scope?.description ?? "",
      taxRateBps: record.scope?.taxRateBps ?? DEFAULT_TAX_RATE_BPS,
      lineItems: (record.scope?.lineItems ?? []).map(
        (item): LineItemDraft => ({
          id: item.id,
          title: item.title ?? "",
          description: item.description ?? "",
          quantity: item.quantity ?? 1,
          unitAmountCents: item.unitAmountCents ?? 0,
        }),
      ),
    },
  };
}

export type * from "../types";
